// 📄 pages/Perfil/MisAusenciasSection.tsx
// Autoservicio de ausencias (vacaciones/permisos/licencias) del usuario —
// dato propio de la persona (usuarios/{uid}/vacaciones), visible a
// cualquier compañero de cualquier proyecto (ver AusenciasEquipo.tsx /
// AusenciasProximaSemanaBanner.tsx). Sin aprobación: se carga y ya queda
// visible, es informativo para planear, no un flujo de RRHH formal.
import { useEffect, useState } from 'react';
import { CalendarDays, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  Vacacion, VacacionData, TipoAusencia, TIPOS_AUSENCIA, tipoAusenciaLabel,
  getVacaciones, crearVacacion, eliminarVacacion, seSuperponeConProximosDias
} from '../../services/vacacionesService.ts';
import ConfirmDialog from '../../components/ui/ConfirmDialog.tsx';

interface MisAusenciasSectionProps {
  uid: string;
}

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

const EMPTY_FORM: VacacionData = { fechaInicio: '', fechaFin: '', tipo: 'vacaciones', notas: '' };

export default function MisAusenciasSection({ uid }: MisAusenciasSectionProps) {
  const [vacaciones, setVacaciones] = useState<Vacacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<VacacionData>(EMPTY_FORM);
  const [mostrandoForm, setMostrandoForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aEliminar, setAEliminar] = useState<Vacacion | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      setVacaciones(await getVacaciones(uid));
    } catch (err) {
      console.error('Error cargando mis ausencias:', err);
      setError('No se pudieron cargar tus ausencias.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [uid]);

  const handleAgregar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      await crearVacacion(uid, form);
      setForm(EMPTY_FORM);
      setMostrandoForm(false);
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la ausencia.');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await eliminarVacacion(uid, aEliminar.id);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error eliminando la ausencia:', err);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-blue-400" /> Mis ausencias
        </h2>
        {!mostrandoForm && (
          <button
            type="button"
            onClick={() => setMostrandoForm(true)}
            className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar
          </button>
        )}
      </div>
      <p className="text-[11px] text-gray-500 -mt-2">
        Visible para cualquier compañero de cualquier proyecto compartido — así pueden planear alrededor de tus fechas.
      </p>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {mostrandoForm && (
        <form onSubmit={handleAgregar} className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Desde</label>
              <input type="date" required value={form.fechaInicio} onChange={(e) => setForm({ ...form, fechaInicio: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Hasta</label>
              <input type="date" required value={form.fechaFin} onChange={(e) => setForm({ ...form, fechaFin: e.target.value })} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Tipo</label>
            <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoAusencia })} className={inputClass}>
              {TIPOS_AUSENCIA.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Notas (opcional)</label>
            <input type="text" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="ej. viaje familiar" className={inputClass} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setMostrandoForm(false); setForm(EMPTY_FORM); }} className="px-3 py-2 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/5 transition-all">
              Cancelar
            </button>
            <button type="submit" disabled={guardando} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3.5 py-2 rounded-lg text-xs font-semibold text-white transition-all">
              {guardando ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : 'Guardar'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando...
        </div>
      ) : vacaciones.length === 0 ? (
        <p className="text-xs text-gray-500 py-2">Todavía no cargaste ninguna ausencia.</p>
      ) : (
        <div className="space-y-2">
          {vacaciones.map((v) => {
            const proxima = seSuperponeConProximosDias(v);
            return (
              <div key={v.id} className="flex items-center justify-between gap-3 bg-black/20 border border-white/10 rounded-xl p-3">
                <div className="min-w-0">
                  <p className="text-sm text-white">
                    {v.fechaInicio} → {v.fechaFin}
                    {proxima && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">Próxima semana</span>}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{tipoAusenciaLabel(v.tipo)}{v.notas ? ` · ${v.notas}` : ''}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAEliminar(v)}
                  className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                  title="Eliminar"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta ausencia?"
          message={`El rango ${aEliminar.fechaInicio} → ${aEliminar.fechaFin} se eliminará.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
